import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiClient } from '@/lib/api';

interface TenantContextType {
  selectedTenantId: string | null;
  setSelectedTenantId: (tenantId: string | null) => void;
  tenantChangeCounter: number;
  isResolvingSubdomain: boolean;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

function getSubdomainTenant(): string | null {
  const hostname = window.location.hostname
  
  // Skip admin, api, and auth subdomains
  const excludedSubdomains = [
    'admin.queuemein.com', 
    'api.queuemein.com', 
    'queuemein.com',
    'auth.queuemein.com',
    'login.queuemein.com',
    'signin.queuemein.com'
  ]
  
  if (excludedSubdomains.includes(hostname)) {
    return null
  }
  
  // Extract subdomain
  if (hostname.endsWith('.queuemein.com')) {
    const subdomain = hostname.replace('.queuemein.com', '')
    // Validate subdomain format
    if (/^[a-zA-Z0-9_-]+$/.test(subdomain)) {
      return subdomain
    }
  }
  
  return null
}

export function TenantProvider({ children }: { children: React.ReactNode }) {
  const subdomainTenant = getSubdomainTenant()
  const [isResolvingSubdomain, setIsResolvingSubdomain] = useState(false)
  
  const [selectedTenantId, setSelectedTenantIdState] = useState<string | null>(() => {
    // If we're on a subdomain, we'll resolve it to actual tenant ID
    if (subdomainTenant) {
      return null // Will be resolved in useEffect
    }
    return localStorage.getItem('qmi-selected-tenant');
  });
  const [tenantChangeCounter, setTenantChangeCounter] = useState(0);

  // Resolve subdomain to actual tenant ID
  useEffect(() => {
    if (subdomainTenant && !selectedTenantId) {
      setIsResolvingSubdomain(true)
      apiClient.resolveSubdomain(subdomainTenant)
        .then(result => {
          console.log('Subdomain resolved:', result)
          setSelectedTenantIdState(result.tenant_id)
          setTenantChangeCounter(prev => prev + 1)
        })
        .catch(error => {
          console.error('Failed to resolve subdomain:', error)
          // Fallback: try using subdomain directly
          setSelectedTenantIdState(subdomainTenant)
          setTenantChangeCounter(prev => prev + 1)
        })
        .finally(() => {
          setIsResolvingSubdomain(false)
        })
    }
  }, [subdomainTenant, selectedTenantId])

  const setSelectedTenantId = (tenantId: string | null) => {
    // If we're on a subdomain, don't allow changing tenant
    if (subdomainTenant) {
      return
    }
  
    setSelectedTenantIdState(tenantId);
    setTenantChangeCounter(prev => prev + 1);
    
    if (tenantId) {
      localStorage.setItem('qmi-selected-tenant', tenantId);
    } else {
      localStorage.removeItem('qmi-selected-tenant');
    }
  };

  return (
    <TenantContext.Provider value={{
      selectedTenantId,
      setSelectedTenantId,
      tenantChangeCounter,
      isResolvingSubdomain
    }}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenantContext() {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenantContext must be used within a TenantProvider');
  }
  return context;
}